import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { SessionInfo, TokenInfo } from "@techplanner/shared/core";
import { useAuth } from "../auth";
import { Alert, Button, Field, Skeleton } from "../components/ui";
import { api, ApiError, errorMessage } from "../lib/api";
import { applyCompanyApp, applyCompanyColor, rememberBranding, rememberCompanyCode } from "../lib/company";
import { CompanyMark } from "./Login";

type Saved = SessionInfo | { ok: true; companyStatus: string };

/** Page ouverte depuis un e-mail : choix du mot de passe (réinitialisation ou invitation). */
export function TokenLinkPage() {
  const { token = "" } = useParams();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [expired, setExpired] = useState(false);

  const info = useQuery({
    queryKey: ["token", token],
    queryFn: async () => {
      const res = await api<TokenInfo>(`/public/tokens/${encodeURIComponent(token)}`);
      // Le téléphone retient la société du lien avant tout affichage.
      rememberCompanyCode(res.company.code);
      rememberBranding(res.company);
      return res;
    },
    staleTime: Infinity,
    gcTime: 0,
    retry: false
  });

  useEffect(() => {
    if (info.data) {
      applyCompanyColor(info.data.company.primaryColor);
      applyCompanyApp(info.data.company);
    }
  }, [info.data]);

  if (info.isPending) {
    return (
      <main className="login">
        <Skeleton count={3} />
      </main>
    );
  }

  if (expired || !info.data) {
    const gone = expired || (info.error instanceof ApiError && info.error.status === 410);
    return (
      <main className="login">
        <img className="login-logo" src="/icons/icon.svg" alt="" width={72} height={72} />
        <h1>TechPlanner</h1>
        <div className="form" style={{ marginTop: 20 }}>
          <Alert kind="error">{gone ? "Ce lien n'est plus valable." : errorMessage(info.error)}</Alert>
          {gone ? (
            <Link to="/mot-de-passe-oublie" className="btn primary large block">
              Recevoir un nouveau lien
            </Link>
          ) : (
            <Button variant="primary" size="large" block onClick={() => void info.refetch()}>
              Réessayer
            </Button>
          )}
        </div>
      </main>
    );
  }

  const { company, purpose, displayName, username } = info.data;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setFieldError(undefined);
    if (password !== confirm) {
      setError("Les deux mots de passe sont différents.");
      return;
    }
    setLoading(true);
    try {
      const res = await api<Saved>(`/public/tokens/${encodeURIComponent(token)}`, { method: "POST", body: { password } });
      if ("user" in res) {
        localStorage.setItem("tp:last-username", res.user.username);
        signIn(res);
        navigate("/", { replace: true });
      } else {
        setPending(true);
      }
    } catch (err) {
      if (err instanceof ApiError && err.fields.password) setFieldError(err.fields.password);
      else if (err instanceof ApiError && err.status === 410) setExpired(true);
      else setError(errorMessage(err));
      setPassword("");
      setConfirm("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login">
      <CompanyMark branding={company} />
      <h1>{purpose === "invite" ? `Bienvenue chez ${company.name}` : "Choisissez votre mot de passe"}</h1>
      <p className="text-2" style={{ marginTop: 6, marginBottom: 28 }}>
        {displayName} · identifiant <strong>{username}</strong>
      </p>
      {pending ? (
        <div className="form">
          <Alert kind="info">Mot de passe enregistré. Vous pourrez vous connecter dès que votre société sera validée.</Alert>
        </div>
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          {error && <Alert kind="error">{error}</Alert>}
          <Field label="Nouveau mot de passe" htmlFor="new-password" error={fieldError} hint="8 caractères minimum.">
            <input
              id="new-password"
              className="input"
              type="password"
              autoComplete="new-password"
              enterKeyHint="next"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>
          <Field label="Confirmez le mot de passe" htmlFor="confirm-password">
            <input
              id="confirm-password"
              className="input"
              type="password"
              autoComplete="new-password"
              enterKeyHint="go"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </Field>
          <Button type="submit" variant="primary" size="large" block loading={loading} disabled={!password || !confirm}>
            Enregistrer
          </Button>
        </form>
      )}
    </main>
  );
}
